import React from "react";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { CRMFilterSearchSelect } from "../../../Complex/CRMFilterSearchSelect/CRMFilterSearchSelect";
import { IFuzzySortOption } from "../../../../hooks/useFuzzysort";
import { CRMFilterAndSelectTinyArrowOptionWrap } from "../../../Simple/CRMFilterAndSelectTinyArrowOptionWrap/CRMFilterAndSelectTinyArrowOptionWrap";
import { CRMFuzzySortSearchHighlight } from "../../../Simple/CRMFuzzySortSearchHighlight/CRMFuzzySortSearchHighlight";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { CRMParagraph } from "../../../Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../../WeightBold/WeightBold";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { Background } from "../../../BuildingBlocks/Background";
import { FontQuicksand } from "../../../BuildingBlocks/FontQuicksand";
import { Text } from "../../../BuildingBlocks/Text";
import { CRMFontSizes } from "../../../../models/CRMFontSizes";
import { CRMColors } from "../../../../models/CRMColors";
import { TCasesPurchasePaymentMethod1 } from "../../../../../../domain/codecs/CasesPurchasePaymentMethod";
import { isTemplateEligibleForCase, TEmailTemplateRecord } from "../../../../../../domain/emailTemplates/templateLibrary";
import { templatesForPicker } from "../../../../../../domain/emailTemplates/templatesForPicker";
import { TEmailTemplatesForm } from "../../../../../../domain/codecs/form/EmailTemplatesForm";
import { TTransactionType1 } from "../../../../../../domain/codecs/TransactionType";
import { TCaseSellerReasonForSale1 } from "../../../../../../domain/codecs/CaseSellerReasonForSale";

export type TEmailTemplateProps = {
    transactionType: TTransactionType1;
    sellerReasonForSale: TCaseSellerReasonForSale1;
    gopDocumentExists: boolean;
    purchasePaymentMethod: TCasesPurchasePaymentMethod1;
    isUnregistered: boolean;
    templatesForm: TEmailTemplatesForm;
    onSelectTemplate: (template: TEmailTemplateRecord) => void;
};

export const CRMComposeEmailTemplateFilterSearchSelect = (props: React.PropsWithChildren<TEmailTemplateProps>): JSX.Element => {

    const gopDocumentExists = props.gopDocumentExists;
    const purchasePaymentMethod = props.purchasePaymentMethod;
    const isUnregistered = props.isUnregistered;

    const getDeadlineContent = () => {
        const stringStart = `The exchange deadline is `;
        let deadlineString = ""
        const stringEnd = `. Anything outside this timeline will need approval from the executor.`;
        if (gopDocumentExists && purchasePaymentMethod === "mortgage") {
            deadlineString = "28 days from the acceptance of the offer"
        }
        if (gopDocumentExists && purchasePaymentMethod === "cash") {
            deadlineString = "14 days from the acceptance of the offer"
        }
        if (gopDocumentExists && purchasePaymentMethod === "unknown") {
            deadlineString = "14 days from the acceptance of the offer if the payment method is cash, 28 days if the payment is with a mortgage"
        }
        if (!gopDocumentExists) {
            deadlineString = "14 days from the grant being received"
        }
        return `\t• ${stringStart}${deadlineString}${stringEnd}\n`;
    };

    const getContractsContent = (isProbate: boolean) => {
        const contentArray = [
            "I hope you are doing well. I am contacting you on behalf of the case handler for the property above. Please find attached the draft contract papers for your reference. Please let us know if you require anything else.\n\nI have included:\n",
            `\t• Draft Contract\n\t• Draft TR1\n\t• Title Plan\n\t• Register\n\t• EPC${isProbate ? "\n\n" : "\n\t• TA6\n\t• TA10\n\n"}`,
            `${isProbate ? "Please note that the seller is an Executor without knowledge of the property. Protocol Documents will not be provided, and the property is sold as seen.\n\n" : ""}`,
            "Your client’s offer has been accepted, conditional to the following:\n",
            isProbate ? getDeadlineContent() : "",
            isProbate && !gopDocumentExists ? "\t• You will raise enquiries before the grant arrives.\n" : "",
            "\t• You use the searches provided by us. Your client will be covered under the personal search insurance. If they expire before completion, we will provide a search indemnity at our expense. \n",
            isUnregistered ? "\t• You will register the property as part of your standard completion process. Our client has not instructed us to register before the sale.\n\tWe’ll provide you with the epitome of the title and assist you with any related requisitions.\n" : "",
            "\t• You will accept electronically signed transfers. We will follow the Land Registry Guidelines and assist you with any requisitions relating to the signing.\n\n",
            "We propose that the matter proceeds under the Law Society Conveyancing Protocol. Please confirm that you will also be adopting the Protocol.\n\n",
            "We request, where possible, please send any documentation to us by email.\n\n",
            "We'll be happy to deal with any questions about the property or the documentation supplied.  We shall not be answering any standard general enquiries.\n\n",
            "Please let us know:\n",
            `\t• The current stage status of your client's related transactions (if any).\n\t• The amount of deposit that will be made available on exchange.\n\t• If your client requires a mortgage, are you in receipt of a mortgage offer?\n\t• Have you completed your client's identification checks?${isProbate ? "\n\n" : "\n\t• Any proposals your client has regarding timescales for exchange and completion.\n\n"}`,
            "Your client is invited to obtain copies of any documents over 20 years old directly from the relevant Local Authority at their own expense.\n\n",
            "Please note we are partnering with Yoti Sign to provide conveyancer-certified electronic signatures in accordance with Practice Guide 82. Please do let us know if you have any concerns relating to this.\n\n",
            "We adopt the Law Society's Code for Completion by Post but vary 13(iii) by providing a certified copy of the executed Transfer by email on completion.\n\n",
            "Please ensure that when sending any money to us, you quote the matter id listed in the subject line of this email.\n\n",
            "In light of William Sindall Plc -v- Cambridgeshire County Council, please note that any replies to enquiries are given to the best knowledge of the Seller, without any responsibility on the part of Sail Legal Limited and having made no attempt to investigate or verify. The information is given on that basis with no warranties, and the Buyer must make their searches, enquiries, and inspections as they consider necessary and appropriate. The replies are believed to be correct, but their accuracy cannot be guaranteed, and they shall not be deemed to imply that the Seller or Sail Legal Limited have made specific searches and enquiries. The Buyer must, but with full notice of the actual state and condition of the property in all respects, take it as it stands, and no guarantee on warranty in respect to the condition is given or shall be implied. The Buyer must buy with full notice of the actual state and condition of the property in all respects and take it as it stands, and no guarantee or warranty in respect of the condition is given or implied. The Buyer is free to conduct such inspection and survey as they consider necessary and must rely in all respects upon such.\n\n",
            "Please note that we do not answer specific queries regarding identification. We have done our due diligence to our satisfaction per the Money Launder Regulations 2017 requirements and the Protocol.\n\n",
            "When making any additional enquiries, please note that by the recommendation of the Law Society’s Standing Committee on Conveyancing, we advise Sellers that they are not required to answer any enquiries which:\n",
            "\t• require them to express an opinion on the state and condition of the property or its appliances\n\t• are about matters where the Buyers must rely on their survey and inspection or\n\t• are about matters that the parties can discuss directly or with the Agents\nAn answer ‘not known’ does not imply we have made enquiries or investigations.\n\n",
            "Please keep in mind that any response in the Protocol forms related to the identification of Japanese Knotweed should be verified independently by the Buyer, as the Seller is not a horticulturist and would not be expected to recognise it.\n\n",
            "To avoid doubt and at all times, undertakings are only undertakings if the word “undertaking” is used, and in the absence of such, no inference of an undertaking should be made or assumed.\n\n",
            "We have no authority to sign a Contract for our client unless we explicitly tell you in writing to the contrary.\n\n",
            "We look forward to hearing from you with the approval of the draft contract."
        ]
        return contentArray.join("");
    };

    const templateConveyancerSellerContractsProbate: TEmailTemplateRecord = {
        "recipient": "Conveyancer",
        "acting_for": "Seller",
        "use_case": "Sending Contracts & TR1s - Probate",
        "body": getContractsContent(true),
    };

    const templateConveyancerSellerContractsNormal: TEmailTemplateRecord = {
        "recipient": "Conveyancer",
        "acting_for": "Seller",
        "use_case": "Sending Contracts & TR1s - Full Title",
        "body": getContractsContent(false),
    };

    // The two contract templates are built by getContractsContent from the case's own
    // flags, so they cannot be rows in the library and are appended here instead.
    const getContractTemplates = (): Array<TEmailTemplateRecord> =>
        props.transactionType !== "sale" ? []
        : props.sellerReasonForSale === "probate" ? [templateConveyancerSellerContractsProbate]
        : props.sellerReasonForSale === "unknown" ? [templateConveyancerSellerContractsNormal, templateConveyancerSellerContractsProbate]
        : [templateConveyancerSellerContractsNormal];

    const getRelevantTemplates = (): Array<TEmailTemplateRecord> => [
        ...getContractTemplates(),
        ...templatesForPicker(props.templatesForm).filter((template) => isTemplateEligibleForCase(template, {
            transactionType: props.transactionType,
            sellerReasonForSale: props.sellerReasonForSale,
            purchasePaymentMethod: props.purchasePaymentMethod,
        })),
    ];

    const PopoutOptions = getRelevantTemplates().map(EmailTemplateRecordToListSearchOption(props.onSelectTemplate));

    return (
        <>
            <CRMFilterSearchSelect
                searchStickyTopPosition="51px"
                searchPlaceholder="Search for a template"
                noMatchesText="There are no attachments to choose from."
                options={PopoutOptions}
            />
        </>
    );
};


export const EmailTemplateRecordToListSearchOption = (onClick: (template: TEmailTemplateRecord) => void) => 
    (template: TEmailTemplateRecord): IFuzzySortOption<string, TEmailTemplateRecord> => {
        const searchLabel = `To: ${template.recipient}, Acting for: ${template.acting_for}, Use case: ${template.use_case}`;

        return {
            label: searchLabel,
            value: template.body,
            meta: template,
            richLabelFormatting: (result, searchText) => (
                <CRMListSearchOption__EmailTemplate
                    result={result}
                    searchText={searchText}
                    template={template}
                    onClick={() => onClick(template)}
                />
            )
        }
    }
;

export type TCRMListSearchOption__EmailTemplate<V extends string> = {
    result: Fuzzysort.KeyResult<IFuzzySortOption<V, TEmailTemplateRecord>>;
    template: TEmailTemplateRecord;
    searchText: string;
    onClick: () => void;
};

export const CRMListSearchOption__EmailTemplate = <V extends string>(props: TCRMListSearchOption__EmailTemplate<V>): JSX.Element => {
    return (
        <CRMFilterAndSelectTinyArrowOptionWrap onClick={props.onClick}>
            <Background padding={`0px 0px ${CRMSpacing.TINY}`}>
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    {/* LABELS & TAG */}
                    <SpacingColumn spacing={CRMSpacing.TINY}>
                        {/* LABELS - TO & FOR */}
                        <SpacingRow 
                            spacing={CRMSpacing.MEDIUM}
                            childSize="200px 1fr"
                        >
                            <OptionLabel
                                label={"To:"}
                                value={props.template.recipient}
                                searchText={props.searchText}
                            />
                            <OptionLabel
                                label={"Acting for:"}
                                value={props.template.acting_for}
                                searchText={props.searchText}
                            />
                        </SpacingRow>

                        {/* TAG - USE CASE */}
                        <div>
                            <TagPill
                                label={props.template.use_case}
                                searchText={props.searchText}
                            />
                        </div>
                    </SpacingColumn>

                    {/* PARAGRAPH - CONTENT PREVIEW */}
                    <Background display="flex" padding={`0px ${CRMSpacing.TINY}`}>
                        <FontQuicksand>
                            <Text 
                                size={CRMFontSizes.SMALL} 
                                lineHeight={1}
                                weight={500}
                                color={CRMColors.NEUTRAL_2}
                            >
                                {props.template.body.slice(0, 100)}...
                            </Text>
                        </FontQuicksand>
                    </Background>
                </SpacingColumn>
            </Background>
        </CRMFilterAndSelectTinyArrowOptionWrap>
    );
}

type TOptionLabelProps = {
    label: string;
    value: string;
    searchText: string;
}

const OptionLabel = (props: TOptionLabelProps) => {
    return (
        <SpacingRow spacing={CRMSpacing.TINY}>
            <CRMParagraph>
                <WeightBold>
                    <CRMFuzzySortSearchHighlight
                        text={props.label}
                        searchText={props.searchText}
                    />
                </WeightBold>
            </CRMParagraph>
            <CRMParagraph>
                <CRMFuzzySortSearchHighlight
                    text={props.value}
                    searchText={props.searchText}
                />
            </CRMParagraph>
        </SpacingRow>
    )
}


type TTagPillProps = {
    label: string;
    searchText: string;
}

const TagPill = (props: TTagPillProps) => {
    return (
        <Background
            display="inline-flex"
            borderRadius={`100px`}
            color={CRMColors.PRIMARY_16}
            padding={`${CRMSpacing.TINY} ${CRMSpacing.SMALL}`}
        >
            <FontQuicksand>
                <Text 
                    size={CRMFontSizes.SMALL} 
                    lineHeight={1}
                    weight={500}
                    color={CRMColors.PRIMARY_2}
                >
                    <CRMFuzzySortSearchHighlight
                        text={props.label}
                        searchText={props.searchText}
                    />
                </Text>
            </FontQuicksand>
        </Background>
    )
}
